import { FocusShapeType, SHAPES_BY_LEVEL } from "@app/_enums/focus-tracker.enum";

const SHOW_DURATION = 3; // seconds each shape is visible
const HIDE_DURATION = 2; // seconds between shapes
const OPTION_COUNT = 12; // 4×3 grid of options

/**
 * Get the active shapes for a given level.
 * Level N uses the first N shape types.
 */
export function getShapesForLevel(level: number): FocusShapeType[] {
    if (level < 1 || level > 10) return [];
    return SHAPES_BY_LEVEL[level];
}

/**
 * Get the total game duration for a level in seconds.
 * Level N = N minutes.
 */
export function getDurationForLevel(level: number): number {
    return level * 60;
}

/**
 * Generate the full sequence of shapes for a game session.
 * Fills the entire duration with show/hide cycles, picking randomly from active shapes.
 */
export function generateShapeSequence(
    level: number,
    seed: number
): { shape: FocusShapeType; showDuration: number; hideDuration: number }[] {
    const shapes = getShapesForLevel(level);
    if (shapes.length === 0) return [];

    const totalSeconds = getDurationForLevel(level);
    const cycleTime = SHOW_DURATION + HIDE_DURATION;
    const shapeCount = Math.floor(totalSeconds / cycleTime);

    const rng = new SeededRandom(seed);
    const sequence: { shape: FocusShapeType; showDuration: number; hideDuration: number }[] = [];

    for (let i = 0; i < shapeCount; i++) {
        const shape = shapes[rng.nextInt(0, shapes.length)];
        sequence.push({ shape, showDuration: SHOW_DURATION, hideDuration: HIDE_DURATION });
    }

    return sequence;
}

/**
 * Generate 12 integer options for the answer grid (4×3).
 * Includes the correct count, with plausible distractors around it.
 */
export function generateOptions(correctCount: number, count: number = OPTION_COUNT): number[] {
    const options = new Set<number>([correctCount]);

    // Generate options spread around the correct answer
    let offset = 1;
    while (options.size < count) {
        // Alternate above and below
        const above = correctCount + offset;
        const below = correctCount - offset;
        if (below >= 0) options.add(below);
        if (options.size < count) options.add(above);
        offset++;
    }

    return Array.from(options).sort((a, b) => a - b);
}

/**
 * Pick a random shape to ask about and return its correct count.
 * @param shapeCounts Map of shape type to count seen during the game
 */
export function pickQuestionShape(
    shapeCounts: Record<string, number>,
    seed: number
): { shape: FocusShapeType; correctCount: number } {
    const entries = Object.entries(shapeCounts).filter(([, count]) => count > 0);
    if (entries.length === 0) {
        return { shape: FocusShapeType.Circle, correctCount: 0 };
    }

    const rng = new SeededRandom(seed);
    const [shape, correctCount] = entries[rng.nextInt(0, entries.length)];
    return { shape: shape as FocusShapeType, correctCount };
}

/**
 * Count occurrences of each shape type in a sequence.
 */
export function countShapes(
    sequence: { shape: FocusShapeType }[]
): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const { shape } of sequence) {
        counts[shape] = (counts[shape] || 0) + 1;
    }
    return counts;
}

// --- Seeded random number generator ---

class SeededRandom {
    private state: number;

    constructor(seed: number) {
        this.state = seed >>> 0;
    }

    next(): number {
        this.state = (this.state + 0x6D2B79F5) & 0xFFFFFFFF;
        let t = this.state;
        t = ((t ^ (t >>> 15)) * (t | 1)) & 0xFFFFFFFF;
        t = (t ^ (t + ((t ^ (t >>> 7)) * (t | 61)))) & 0xFFFFFFFF;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    nextInt(min: number, max: number): number {
        return Math.floor(this.next() * (max - min)) + min;
    }
}
